import {
  FormEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { useAuth } from "@/auth/AuthContext";
import {
  TimeRangeDropdown,
  type TimeRangeValue,
  resolveTimeRange,
} from "@/components/MarketShared";
import type {
  Client,
  ClientListResponse,
  DealClientRef,
  CommissionTotals,
  Deal,
  DealBoard,
  DealComment,
  DealCommentListResponse,
  DealDeparture,
  DealDepartureDay,
  DealDepartureListResponse,
  DealStage,
  DealStats,
  DealTypeStats,
  TeamMember,
  UserRole,
} from "@/types";
import { DEAL_STAGES, roleLabel } from "@/types";

/** Roles allowed to see the stats panel + the assigned-broker dropdown.
 *  Manager / director / owner — same gate as the backend's
 *  ``require_manager_or_owner`` dep. */
const MANAGER_PLUS: ReadonlySet<UserRole> = new Set<UserRole>([
  "executive_manager",
  "operations_manager",
  "lead_broker",
]);

/** Per-stage visual tokens. We deliberately ship NO tinted column
 *  background — that produced a rainbow board that read as playful, not
 *  data-serious. The colour signal lives in one place: a small dot next
 *  to the stage label. Everything else is the same neutral dark card. */
const STAGE_META: Record<
  DealStage,
  { label: string; accent: string; dot: string }
> = {
  planning: {
    label: "Planning",
    accent: "text-blue-300",
    dot: "bg-blue-400",
  },
  booked: {
    label: "Booked",
    accent: "text-cyan-300",
    dot: "bg-cyan-400",
  },
  decision_pending: {
    label: "Decision pending",
    accent: "text-amber-300",
    dot: "bg-amber-400",
  },
  done: {
    label: "Done",
    accent: "text-emerald-300",
    dot: "bg-emerald-400",
  },
  lost: {
    label: "Lost",
    accent: "text-rose-300",
    dot: "bg-rose-400",
  },
};

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GEL: "₾",
};

function fmtMoney(v: string | null, cur: string | null): string {
  if (!v) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  const compact =
    Math.abs(n) >= 1_000_000
      ? (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M"
      : Math.abs(n) >= 10_000
        ? (n / 1_000).toFixed(0) + "k"
        : Math.round(n).toLocaleString();
  if (!cur) return compact;
  const sym = CURRENCY_SYMBOL[cur];
  return sym ? `${sym}${compact}` : `${compact} ${cur}`;
}

/** Same as fmtMoney but never abbreviates — for column totals where the
 *  user wants to see exact stakes. */
function fmtMoneyFull(v: string | number | null, cur: string | null): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = Math.round(n).toLocaleString();
  if (!cur) return s;
  const sym = CURRENCY_SYMBOL[cur];
  return sym ? `${sym}${s}` : `${s} ${cur}`;
}

// ── Commission currency switch ───────────────────────────────────────────
// A GEL deal and a USD deal can't be summed into one number, so the backend
// returns a per-currency breakdown plus approximate GEL/USD combined totals
// (latest NBG rate). The user flips which approximate currency to show; the
// choice persists across reloads. Default GEL — Georgian agency.
type CommissionCcy = "GEL" | "USD";
const COMMISSION_CCY_KEY = "deals.commissionCcy";

function normalizeCommissionCcy(v: string | null): CommissionCcy {
  return v === "USD" ? "USD" : "GEL";
}

/** The approximate combined total in the chosen currency, or null when the
 *  FX cache is cold / a bucket couldn't be converted. */
function approxIn(t: CommissionTotals, ccy: CommissionCcy): string | null {
  return ccy === "USD" ? t.approx_usd : t.approx_gel;
}

/** Native per-currency breakdown ("₾120k · $45k") + an approximate combined
 *  total in the chosen currency ("≈ ₾245k"). A lone bucket already in the
 *  chosen currency IS the exact total, so we skip the redundant "≈" line.
 *  ``compact`` (broker leaderboard / type rows) shows just the approx and
 *  moves the breakdown to the hover title. */
function CommissionTotalsView({
  totals,
  ccy,
  compact = false,
  className,
}: {
  totals: CommissionTotals;
  ccy: CommissionCcy;
  compact?: boolean;
  className?: string;
}) {
  const { by_currency } = totals;
  const approx = approxIn(totals, ccy);
  const sym = CURRENCY_SYMBOL[ccy] ?? "";
  const breakdownText = by_currency.length
    ? by_currency.map((b) => fmtMoneyFull(b.total, b.currency)).join(" · ")
    : "—";
  const singleNative =
    by_currency.length === 1 && by_currency[0].currency === ccy;

  if (compact) {
    const text =
      by_currency.length === 0
        ? "—"
        : singleNative || approx == null
          ? breakdownText
          : `≈ ${sym}${fmtMoneyFull(approx, null)}`;
    return (
      <span className={className} title={breakdownText}>
        {text}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-baseline flex-wrap gap-x-1.5 ${className ?? ""}`}>
      {by_currency.length > 0 ? (
        by_currency.map((b, i) => (
          <span key={b.currency}>
            {fmtMoneyFull(b.total, b.currency)}
            {i < by_currency.length - 1 && (
              <span className="text-slate-700 ml-1.5">·</span>
            )}
          </span>
        ))
      ) : (
        <span>—</span>
      )}
      {by_currency.length > 0 && !singleNative && approx != null && (
        <span className="text-slate-500 text-[0.92em]">
          ≈ {sym}
          {fmtMoneyFull(approx, null)}
        </span>
      )}
    </span>
  );
}

/** Tiny ₾ | $ segmented control that drives every approximate total on the
 *  page. */
function CommissionCcyToggle({
  value,
  onChange,
}: {
  value: CommissionCcy;
  onChange: (v: CommissionCcy) => void;
}) {
  return (
    <span className="inline-flex items-center rounded-md border border-white/10 overflow-hidden text-[10px] font-semibold leading-none">
      {(["GEL", "USD"] as CommissionCcy[]).map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          aria-pressed={value === c}
          title={`Show approximate total in ${c}`}
          className={`px-1.5 py-1 transition ${
            value === c
              ? "bg-white/10 text-slate-100"
              : "text-slate-500 hover:text-slate-300"
          }`}
        >
          {CURRENCY_SYMBOL[c]}
        </button>
      ))}
    </span>
  );
}

/** When did the deal enter its current stage? Falls back to created_at /
 *  updated_at if the stage timestamp wasn't recorded (older rows). */
function stageEnteredAt(deal: Deal): string {
  switch (deal.stage) {
    case "planning":
      return deal.planning_at || deal.created_at;
    case "booked":
      return deal.booked_at || deal.updated_at;
    case "decision_pending":
      return deal.decision_pending_at || deal.updated_at;
    case "done":
      return deal.done_at || deal.updated_at;
    case "lost":
      return deal.lost_at || deal.updated_at;
    default:
      return deal.created_at;
  }
}

function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / (24 * 3600 * 1000)));
}

function ageLabel(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

/** Render an ISO ``YYYY-MM-DD`` deal date in the user's locale, drops the
 *  year when it matches the current calendar year so the card stays
 *  compact ("May 13" vs "May 13, 2026"). */
function fmtDealDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** ── Departures ("leaving the office at 15:00") ────────────────────────
 *
 *  A departure is a wall-clock appointment, so everything here works in the
 *  viewer's local zone — which for this agency is Tbilisi, the same clock
 *  the broker reads off their phone. The backend stores UTC and the two
 *  ``<input>``s below round-trip through it.
 */

/** Local ``YYYY-MM-DD`` for a Date — ``toISOString().slice(0, 10)`` is
 *  wrong here: it shifts to UTC, so 00:30 Tbilisi reports yesterday. */
function localDateKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Split a stored instant into the ``date`` + ``time`` input values that
 *  produced it, so editing a logged trip starts from what's on the row. */
function departureInputParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { date: localDateKey(d), time: `${hh}:${mm}` };
}

/** ``2026-09-21`` + ``15:00`` → an ISO instant. ``new Date("…T15:00")``
 *  (no trailing Z) is parsed as local time, which is exactly the reading
 *  we want: the broker means 15:00 on their own clock. Returns null on an
 *  incomplete pair so the caller can keep the Add button disabled. */
function buildLeaveAt(dateStr: string, timeStr: string): string | null {
  if (!dateStr || !timeStr) return null;
  const d = new Date(`${dateStr}T${timeStr}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** ``15:00`` — the time alone, for rows that already carry their date. */
function fmtDepartureClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** ``today 15:00`` / ``tomorrow 11:00`` / ``Thu 11:00`` / ``Sep 24 11:00``.
 *  Day-relative for the near week (that's the window a broker plans in),
 *  absolute past that. */
function fmtDepartureWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const clock = fmtDepartureClock(iso);
  const today = new Date();
  const dayKey = localDateKey(d);
  if (dayKey === localDateKey(today)) return `today ${clock}`;
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dayKey === localDateKey(tomorrow)) return `tomorrow ${clock}`;
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dayKey === localDateKey(yesterday)) return `yesterday ${clock}`;
  const diffDays = Math.round(
    (d.getTime() - today.getTime()) / (24 * 3600 * 1000),
  );
  if (diffDays > 0 && diffDays < 7) {
    return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${clock}`;
  }
  const sameYear = d.getFullYear() === today.getFullYear();
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  return `${date} ${clock}`;
}

/** Colour for a departure time. Departed-but-still-open is the state worth
 *  interrupting someone over (the broker is out, or forgot to move the
 *  card), so it goes rose; the next hour goes amber as a heads-up; anything
 *  further out, or on a closed deal, stays quiet. */
function departureTone(iso: string, stage: DealStage): string {
  if (stage === "done" || stage === "lost") return "text-slate-500";
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return "text-slate-500";
  if (ms < 0) return "text-rose-300";
  if (ms < 3600 * 1000) return "text-amber-300";
  return "text-cyan-300";
}

/** Small clock glyph shared by the card badge, the strip and the inspector. */
function ClockIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`w-3 h-3 flex-shrink-0 ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15.5 14" />
    </svg>
  );
}

/** Cards turn warmer (yellow → red) the longer they've been sitting in
 *  an open stage. Closed stages (done / lost) don't decay. */
function ageTone(stage: DealStage, days: number): string {
  if (stage === "done" || stage === "lost") return "text-slate-500";
  if (days >= 14) return "text-rose-300";
  if (days >= 7) return "text-amber-300";
  return "text-slate-500";
}

/** Average days the visible cards in a column have spent in that stage —
 *  drives the small "12d avg" indicator next to the column count. Returns
 *  null on an empty column so the UI can suppress the badge. */
function avgDaysInStage(items: Deal[]): number | null {
  if (items.length === 0) return null;
  const total = items.reduce(
    (sum, d) => sum + daysSince(stageEnteredAt(d)),
    0,
  );
  return Math.round(total / items.length);
}

/** Sum deal values per currency. Skips deals without a price/currency. */
function totalsByCurrency(items: Deal[]): Array<{ currency: string; total: number }> {
  const map = new Map<string, number>();
  for (const d of items) {
    if (!d.price || !d.currency) continue;
    const n = Number(d.price);
    if (!Number.isFinite(n)) continue;
    map.set(d.currency, (map.get(d.currency) || 0) + n);
  }
  return Array.from(map.entries())
    .map(([currency, total]) => ({ currency, total }))
    .sort((a, b) => b.total - a.total);
}

const DEAL_TYPE_META: Record<"sale" | "rent", { label: string; cls: string }> = {
  sale: {
    label: "Sale",
    cls: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300",
  },
  rent: {
    label: "Rent",
    cls: "border-teal-500/30 bg-teal-500/10 text-teal-300",
  },
};

export function Deals() {
  const { user } = useAuth();
  const [board, setBoard] = useState<DealBoard | null>(null);
  const [stats, setStats] = useState<DealStats | null>(null);
  // Stats panel starts collapsed — managers expand it when they want the
  // funnel breakdown; brokers don't see the panel at all. Collapsing by
  // default keeps the kanban above the fold on a normal-height screen.
  const [statsExpanded, setStatsExpanded] = useState(false);
  // Time-range filter (toolbar) doesn't touch the open columns — the
  // backend's ``_activity_window_clause`` only narrows Done / Lost rows
  // by their close timestamp. Within those two terminal columns we also
  // cap the rendered cards at five by default so a busy broker doesn't
  // see a graveyard; this Set tracks which terminal columns the broker
  // has clicked "Show more" on. Per-column, so expanding Done doesn't
  // also expand Lost.
  const [expandedClosed, setExpandedClosed] = useState<Set<DealStage>>(
    () => new Set(),
  );
  function toggleClosedExpansion(stage: DealStage) {
    setExpandedClosed((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }
  // How many cards to surface in a closed column before the "Show more"
  // toggle kicks in. Tuned to match what fits above the fold without
  // scrolling the column body on a normal-height screen.
  const CLOSED_PREVIEW_LIMIT = 5;
  const [error, setError] = useState<string | null>(null);
  // Today's logged trips across the team — drives the "Out today" strip.
  // Independent of the toolbar filters (it's always the current day), but
  // reloaded with the board so adding a trip in the inspector shows up.
  const [departuresToday, setDeparturesToday] =
    useState<DealDepartureDay | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Deal | null>(null);
  const [deleting, setDeleting] = useState<Deal | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [mineOnly, setMineOnly] = useState(false);
  const [search, setSearch] = useState("");
  // Default 1-week window — picks up activity (open deals stay visible
  // regardless; won/lost rows age off the board once their close
  // timestamp leaves the window). Honoured by the board + stats calls.
  // Default to the current calendar month (1st → today), not a rolling
  // 30-day window — matches how the team thinks about monthly deal targets.
  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ mode: "this_month" });
  // Optional drill-down for managers / owners. Empty string = "everyone
  // I can see". Hidden for broker / member (the backend would 403 them
  // on any other user_id anyway, but no need to render the control).
  const [selectedBrokerId, setSelectedBrokerId] = useState<string>("");
  // Sale / Rent toolbar filter. Empty string = "both" (no filter). Sent
  // to the backend so the board + stats agree on what's in scope — the
  // stats panel's ``by_type`` row still shows the breakdown either way.
  const [dealTypeFilter, setDealTypeFilter] = useState<"" | "sale" | "rent">(
    "",
  );
  // Which currency the approximate commission totals render in (₾ | $).
  // Persisted so the broker's preference survives reloads.
  const [commCcy, setCommCcyState] = useState<CommissionCcy>(() => {
    if (typeof window === "undefined") return "GEL";
    return normalizeCommissionCcy(window.localStorage.getItem(COMMISSION_CCY_KEY));
  });
  const setCommCcy = useCallback((next: CommissionCcy) => {
    setCommCcyState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COMMISSION_CCY_KEY, next);
    }
  }, []);

  const isManagerPlus = user ? MANAGER_PLUS.has(user.role) : false;
  // ``?newClient=<id>`` (set by the "+ New deal" button on a client's
  // detail page) auto-opens the create modal preselected to that
  // client. We consume the param once on mount, then strip it from the
  // URL so a back-button trip doesn't keep re-opening the modal.
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillClientId, setPrefillClientId] = useState<string | null>(null);
  useEffect(() => {
    const id = searchParams.get("newClient");
    if (!id) return;
    setPrefillClientId(id);
    setCreating(true);
    const next = new URLSearchParams(searchParams);
    next.delete("newClient");
    setSearchParams(next, { replace: true });
    // We only want this to run once on mount; ignore exhaustive-deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set of user IDs the team list says are online. Derived once per
  // teamMembers change so each DealCard does an O(1) lookup. Empty for
  // viewers who can't load /api/team — the cards just skip the dot.
  const onlineUserIds = useMemo(
    () => new Set(teamMembers.filter((m) => m.is_online).map((m) => m.id)),
    [teamMembers],
  );

  /** Build the shared filter query string for the board + stats calls.
   *  ``listed_since`` / ``listed_until`` from ``resolveTimeRange`` are
   *  re-keyed to ``since`` / ``until`` (the deals API names; the time
   *  helper is shared with Market which uses portal-listing semantics). */
  const filterQuery = useCallback(() => {
    const params = new URLSearchParams();
    const tr = resolveTimeRange(timeRange);
    if (tr.listed_since) params.set("since", tr.listed_since);
    if (tr.listed_until) params.set("until", tr.listed_until);
    if (selectedBrokerId) params.set("user_id", selectedBrokerId);
    if (dealTypeFilter) params.set("deal_type", dealTypeFilter);
    return params.toString();
  }, [timeRange, selectedBrokerId, dealTypeFilter]);

  const load = useCallback(async () => {
    const qs = filterQuery();
    try {
      const [b, s, dep] = await Promise.all([
        api<DealBoard>(`/api/deals/board${qs ? `?${qs}` : ""}`),
        isManagerPlus
          ? api<DealStats>(`/api/deals/stats${qs ? `?${qs}` : ""}`).catch(
              () => null,
            )
          : Promise.resolve(null),
        // Only manager+ renders the strip, so don't spend the round-trip
        // for a broker. The endpoint itself is open to every CRM role and
        // scopes to who they can see.
        isManagerPlus
          ? api<DealDepartureDay>("/api/deals/departures/today").catch(
              () => null,
            )
          : Promise.resolve(null),
      ]);
      setBoard(b);
      setStats(s);
      setDeparturesToday(dep);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load deals");
    }
  }, [filterQuery, isManagerPlus]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!user) return;
    // ``/api/team`` is gated to manager / director / owner server-side, so
    // we mirror that check here and let lower roles fall through to the
    // empty default (the dropdown + DealFormModal degrade gracefully).
    if (MANAGER_PLUS.has(user.role)) {
      api<TeamMember[]>("/api/team")
        .then(setTeamMembers)
        .catch(() => setTeamMembers([]));
    }
    api<ClientListResponse>("/api/clients?limit=200")
      .then((r) => setClients(r.items))
      .catch(() => setClients([]));
  }, [user]);

  async function moveDeal(deal: Deal, stage: DealStage) {
    if (deal.stage === stage) return;
    // Optimistic — drop the card into the new column instantly so drag/menu
    // moves don't wait on the network. Reconciled by load() afterwards
    // (server fills in server-side timestamps + recomputes won totals).
    setBoard((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stages: prev.stages.map((b) => {
          if (b.stage === deal.stage) {
            return {
              ...b,
              items: b.items.filter((d) => d.id !== deal.id),
              count: Math.max(0, b.count - 1),
            };
          }
          if (b.stage === stage) {
            return {
              ...b,
              items: [{ ...deal, stage }, ...b.items],
              count: b.count + 1,
            };
          }
          return b;
        }),
      };
    });
    try {
      await api(`/api/deals/${deal.id}`, {
        method: "PATCH",
        json: { stage },
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to update deal");
      // Revert by re-fetching the canonical board state.
      await load();
    }
  }

  const [draggingDeal, setDraggingDeal] = useState<Deal | null>(null);
  const sensors = useSensors(
    // 6px activation lets a click still register as a click (opens edit
    // modal); anything past that is a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  function handleDragStart(e: DragStartEvent) {
    const data = e.active.data.current as { deal?: Deal } | undefined;
    setDraggingDeal(data?.deal ?? null);
  }

  function handleDragEnd(e: DragEndEvent) {
    setDraggingDeal(null);
    const deal = (e.active.data.current as { deal?: Deal } | undefined)?.deal;
    const overId = e.over?.id;
    if (!deal || !overId) return;
    const target = String(overId);
    if (!target.startsWith("col:")) return;
    const stage = target.slice(4) as DealStage;
    if (!DEAL_STAGES.includes(stage) || stage === deal.stage) return;
    moveDeal(deal, stage);
  }

  /** Apply the page-level mine/search filters to a column's items. */
  function filterItems(items: Deal[]): Deal[] {
    const q = search.trim().toLowerCase();
    return items.filter((d) => {
      if (mineOnly && d.assigned_user_id !== user?.id) return false;
      if (!q) return true;
      const haystack = [
        d.property_title,
        d.client?.full_name,
        d.client?.phone,
        d.assigned_user?.full_name,
        d.assigned_user?.email,
        d.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }

  const totalOpen =
    board?.stages
      .filter((s) => s.stage !== "done" && s.stage !== "lost")
      .reduce((sum, s) => sum + filterItems(s.items).length, 0) ?? 0;

  return (
    <div className="w-full">
      <PageHeader
        title="Deals"
        subtitle={
          <span>
            {board ? (
              <span className="inline-flex items-baseline gap-x-3 text-slate-400 tabular-nums">
                <span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">
                    Open
                  </span>
                  <span className="text-slate-200 font-semibold">{totalOpen}</span>
                </span>
                <span className="text-slate-700">·</span>
                <span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">
                    Done
                  </span>
                  <span className="text-emerald-300 font-semibold">
                    {board.done_count}
                  </span>
                </span>
                <span className="text-slate-700">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">
                    Commission
                  </span>
                  <span className="text-emerald-200 font-semibold">
                    <CommissionTotalsView totals={board.done_value} ccy={commCcy} />
                  </span>
                  <CommissionCcyToggle value={commCcy} onChange={setCommCcy} />
                </span>
              </span>
            ) : (
              "Funnel from claim to close."
            )}
          </span>
        }
      />

      {isManagerPlus && (
        <DealStatsPanel
          stats={stats}
          expanded={statsExpanded}
          onToggle={() => setStatsExpanded((v) => !v)}
          timeRangeLabel={describeTimeRange(timeRange)}
          viewerId={user?.id}
          commCcy={commCcy}
        />
      )}

      {isManagerPlus && departuresToday && departuresToday.items.length > 0 && (
        <DeparturesStrip
          day={departuresToday}
          onOpenDeal={(dealId) => {
            const found = board?.stages
              .flatMap((b) => b.items)
              .find((d) => d.id === dealId);
            if (found) setEditing(found);
          }}
        />
      )}

      {/* Filter toolbar — one row, all controls share the same pill
          spec: h-9, rounded-lg, 12px font, ink-900/60 surface, white/10
          border. ``TimeRangeDropdown`` and ``BrokerFilter`` are styled
          to match. On narrow viewports the inner row scrolls horizontally
          rather than wrapping (a wrapped toolbar reads as messy); the
          primary CTA stacks below on phone-sized screens only. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto sm:overflow-visible">
          <div className="relative flex-shrink-0 w-full sm:w-72">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              placeholder="Search title, client, broker…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 bg-ink-900/60 border border-white/10 rounded-lg pl-9 pr-3 text-[12px] text-slate-200 placeholder:text-slate-500 transition focus:outline-none focus:border-white/25"
            />
          </div>
          <TimeRangeDropdown value={timeRange} onChange={setTimeRange} showMonths />
          <DealTypeFilter value={dealTypeFilter} onChange={setDealTypeFilter} />
          {isManagerPlus && teamMembers.length > 0 && (
            <BrokerFilter
              value={selectedBrokerId}
              onChange={setSelectedBrokerId}
              teamMembers={teamMembers}
              viewerId={user?.id}
            />
          )}
          {/* Brokers and members already only see their own deals
              (the backend's ``visible_user_ids`` enforces it), so a
              Mine-only toggle is a no-op for them. Manager+ sees their
              team's deals — that's where the filter actually does
              something. */}
          {isManagerPlus && (
            <button
              type="button"
              onClick={() => setMineOnly((m) => !m)}
              aria-pressed={mineOnly}
              className={
                "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors flex-shrink-0 " +
                (mineOnly
                  ? "bg-accent-500/15 text-accent-100 border border-accent-500/40 shadow-sm shadow-accent-500/10"
                  : "bg-ink-900/60 text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100")
              }
              title="Show only deals assigned to you"
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  mineOnly ? "bg-accent-400" : "bg-slate-600"
                }`}
              />
              Mine only
            </button>
          )}
        </div>
        <button
          className="inline-flex items-center justify-center h-9 px-3.5 rounded-lg text-[12px] font-medium bg-accent-600 hover:bg-accent-500 text-white whitespace-nowrap flex-shrink-0 transition-colors"
          onClick={() => setCreating(true)}
        >
          + New deal
        </button>
      </div>

      {error && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm mb-4">
          {error}
        </div>
      )}

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDraggingDeal(null)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {DEAL_STAGES.map((stage) => {
            const bucket = board?.stages.find((s) => s.stage === stage);
            const visibleItems = bucket ? filterItems(bucket.items) : [];
            // Done / Lost columns get a "first 5, then a Show-more
            // toggle" treatment so a busy broker isn't staring at a wall
            // of closed cards. The toolbar's time range already scopes
            // these (backend filter); the cap is just visual housekeeping
            // on top of whatever the window returned. Open columns
            // (Planning / Booked / Decision pending) always show every
            // card the backend sent — they're the working set.
            const isClosed = stage === "done" || stage === "lost";
            const isExpanded = expandedClosed.has(stage);
            const displayItems =
              isClosed &&
              !isExpanded &&
              visibleItems.length > CLOSED_PREVIEW_LIMIT
                ? visibleItems.slice(0, CLOSED_PREVIEW_LIMIT)
                : visibleItems;
            const hiddenCount = visibleItems.length - displayItems.length;
            // Totals + avg-age use the FULL visible set so the column
            // header / footer always reflect the actual window, not just
            // what's currently rendered.
            const totals = totalsByCurrency(visibleItems);
            const meta = STAGE_META[stage];
            const avgAge = avgDaysInStage(visibleItems);
            return (
              <DroppableColumn key={stage} stage={stage}>
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                    <span
                      className={`text-[11px] font-semibold uppercase tracking-wider truncate ${meta.accent}`}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-slate-500 tabular-nums flex-shrink-0">
                      {visibleItems.length}
                      {bucket && bucket.count !== visibleItems.length && (
                        <span className="text-slate-700"> / {bucket.count}</span>
                      )}
                    </span>
                  </div>
                  {avgAge !== null &&
                    stage !== "done" &&
                    stage !== "lost" && (
                      <span
                        className={`text-[10px] tabular-nums flex-shrink-0 ${
                          avgAge >= 14
                            ? "text-rose-300/80"
                            : avgAge >= 7
                              ? "text-amber-300/80"
                              : "text-slate-500"
                        }`}
                        title={`Average age of cards in this stage`}
                      >
                        {avgAge}d avg
                      </span>
                    )}
                </div>
                <div className="space-y-2">
                  {!board &&
                    Array.from({ length: 2 }).map((_, i) => (
                      <Skeleton key={i} className="h-16" />
                    ))}
                  {board && visibleItems.length === 0 && (
                    <div className="border border-dashed border-white/8 rounded-md py-5 text-center text-[11px] text-slate-600">
                      {bucket && bucket.items.length > 0
                        ? "No matches"
                        : "Drop a card here"}
                    </div>
                  )}
                  {displayItems.map((d) => (
                    <DraggableCard key={d.id} deal={d}>
                      <DealCard
                        deal={d}
                        onlineUserIds={onlineUserIds}
                        onOpen={() => setEditing(d)}
                        onMove={(next) => moveDeal(d, next)}
                        onDelete={() => setDeleting(d)}
                      />
                    </DraggableCard>
                  ))}
                </div>
                {isClosed &&
                  visibleItems.length > CLOSED_PREVIEW_LIMIT && (
                    <button
                      type="button"
                      onClick={() => toggleClosedExpansion(stage)}
                      className="mt-2 w-full text-[10.5px] text-slate-500 hover:text-slate-300 py-1.5 rounded transition hover:bg-white/[0.03]"
                    >
                      {isExpanded
                        ? "Show less"
                        : `Show more (${hiddenCount})`}
                    </button>
                  )}
                {totals.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-white/5 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 tabular-nums">
                    <span className="text-[9.5px] font-semibold uppercase tracking-wider text-slate-500">
                      Total
                    </span>
                    {totals.map((t, i) => (
                      <span key={t.currency} className="text-[10.5px] text-slate-400">
                        {fmtMoneyFull(t.total, t.currency)}
                        {i < totals.length - 1 && (
                          <span className="text-slate-700 ml-2">·</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </DroppableColumn>
            );
          })}
        </div>
        <DragOverlay dropAnimation={null}>
          {draggingDeal ? (
            <div className="opacity-90 rotate-2 shadow-2xl">
              <DealCard
                deal={draggingDeal}
                onOpen={() => {}}
                onMove={() => {}}
                onDelete={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {(creating || editing) && (
        <DealFormModal
          deal={editing}
          teamMembers={teamMembers}
          clients={clients}
          viewerRole={
            // Shareholder is a full-access apex — behave as executive here.
            user?.role === "shareholder" ? "executive_manager" : (user?.role ?? "broker")
          }
          initialClientId={editing ? null : prefillClientId}
          onClientCreated={(c) =>
            setClients((prev) =>
              prev.some((x) => x.id === c.id) ? prev : [c, ...prev],
            )
          }
          onDeparturesChanged={load}
          onClose={() => {
            setCreating(false);
            setEditing(null);
            setPrefillClientId(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            setPrefillClientId(null);
            await load();
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this deal?"
        description={
          deleting ? (
            <>
              <span className="font-semibold text-slate-200">
                {deleting.property_title || "(untitled)"}
              </span>{" "}
              will be removed. The linked hot offer / placement stays in its own
              list.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await api(`/api/deals/${deleting.id}`, { method: "DELETE" });
            setDeleting(null);
            await load();
          } catch (e) {
            setError(e instanceof ApiError ? e.message : "Delete failed");
            setDeleting(null);
          }
        }}
      />
    </div>
  );
}

/** "Out today" — every trip the visible team logged for the current day,
 *  split into already-departed and still-upcoming. Manager+ only (the page
 *  gates it); rendered above the toolbar because it answers a question that
 *  isn't about any one column: who is out of the office right now.
 *
 *  Only open deals reach here (the endpoint filters done / lost out), and
 *  open cards are always on the board regardless of the time window — so a
 *  chip normally resolves to a card. With a broker filter active it may
 *  not, and the click is then a no-op rather than a dead end that errors. */
function DeparturesStrip({
  day,
  onOpenDeal,
}: {
  day: DealDepartureDay;
  onOpenDeal: (dealId: string) => void;
}) {
  // Split against the server's instant, not the browser's: the ordering
  // came from the server and a skewed client clock would disagree with it.
  const nowMs = new Date(day.now).getTime();
  const departed = day.items.filter(
    (i) => new Date(i.leave_at).getTime() <= nowMs,
  );
  const upcoming = day.items.filter(
    (i) => new Date(i.leave_at).getTime() > nowMs,
  );

  function chip(item: DealDepartureDay["items"][number], isPast: boolean) {
    const who = item.user?.full_name || item.user?.email || "—";
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onOpenDeal(item.deal.id)}
        title={
          `${who} · ${new Date(item.leave_at).toLocaleString()} · ` +
          `${item.deal.property_title || "(untitled)"}` +
          (item.note ? ` · ${item.note}` : "")
        }
        className="inline-flex items-center gap-1.5 h-7 pl-1.5 pr-2.5 rounded-lg bg-ink-900/60 border border-white/10 hover:border-white/25 transition-colors max-w-[260px]"
      >
        <Avatar
          size="xs"
          name={item.user?.full_name ?? null}
          email={item.user?.email ?? null}
          url={item.user?.avatar_url ?? null}
        />
        <span
          className={`text-[11px] font-semibold tabular-nums ${
            isPast ? "text-rose-300" : "text-cyan-300"
          }`}
        >
          {fmtDepartureClock(item.leave_at)}
        </span>
        <span className="text-[11px] text-slate-400 truncate min-w-0">
          {(who || "").split(" ")[0]}
        </span>
        <span className="text-slate-700 text-[11px]">·</span>
        <span className="text-[11px] text-slate-500 truncate min-w-0">
          {item.deal.property_title || "(untitled)"}
        </span>
      </button>
    );
  }

  return (
    <div className="card p-3 mb-4 border-white/8">
      <div className="flex items-center gap-2 mb-2.5">
        <ClockIcon className="text-slate-500" />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          Out today
        </span>
        <span className="text-[10.5px] text-slate-500 tabular-nums">
          {departed.length > 0 && (
            <span className="text-rose-300/90">{departed.length} gone</span>
          )}
          {departed.length > 0 && upcoming.length > 0 && (
            <span className="text-slate-700"> · </span>
          )}
          {upcoming.length > 0 && (
            <span className="text-cyan-300/90">{upcoming.length} upcoming</span>
          )}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {departed.map((i) => chip(i, true))}
        {upcoming.map((i) => chip(i, false))}
      </div>
    </div>
  );
}

function DroppableColumn({
  stage,
  children,
}: {
  stage: DealStage;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${stage}` });
  return (
    <div
      ref={setNodeRef}
      className={
        "card p-3 border border-white/8 bg-white/[0.015] min-h-[180px] transition" +
        (isOver ? " ring-2 ring-accent-500/60 border-accent-500/40" : "")
      }
    >
      {children}
    </div>
  );
}

function DraggableCard({ deal, children }: { deal: Deal; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card:${deal.id}`,
    data: { deal },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={isDragging ? { opacity: 0.4 } : undefined}
    >
      {children}
    </div>
  );
}

interface DealCardProps {
  deal: Deal;
  /** User-IDs known to be online right now. Optional — when the viewer
   *  isn't allowed to load /api/team this is empty and no presence dot
   *  appears, which is fine: we degrade quietly rather than guess. */
  onlineUserIds?: Set<string>;
  onOpen: () => void;
  onMove: (s: DealStage) => void;
  onDelete: () => void;
}

function DealCard({ deal, onlineUserIds, onOpen, onMove, onDelete }: DealCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const currentIdx = DEAL_STAGES.indexOf(deal.stage);
  const prevStage = currentIdx > 0 ? DEAL_STAGES[currentIdx - 1] : null;
  const nextOpen = currentIdx < 3 ? DEAL_STAGES[currentIdx + 1] : null;

  const days = daysSince(stageEnteredAt(deal));
  const ageCls = ageTone(deal.stage, days);
  const dealType = deal.deal_type ? DEAL_TYPE_META[deal.deal_type] : null;
  const commission =
    deal.commission_amount ||
    (deal.commission_pct && deal.price
      ? String(Number(deal.price) * Number(deal.commission_pct) / 100)
      : null);
  const hasUrl = !!deal.property_url;

  // Stop drag activation on inner interactive elements.
  const stopPointer = (e: ReactPointerEvent) => e.stopPropagation();

  return (
    <div
      className="group relative bg-ink-850 hover:bg-ink-800 ring-1 ring-transparent hover:ring-accent-500/30 rounded-lg p-3 transition cursor-pointer shadow-sm shadow-black/20"
      onClick={onOpen}
    >
      {/* Row 1 — deal-type chip on the left, hover-only URL + menu on the
          right. Pulling the title out of this row gave the card a clear
          two-line headline instead of a chip-title-icon-icon traffic jam. */}
      <div className="flex items-center gap-1.5 mb-2 h-5">
        {dealType && (
          <span
            className={`text-[9.5px] font-semibold uppercase tracking-wider px-1.5 py-[1.5px] rounded border whitespace-nowrap ${dealType.cls}`}
          >
            {dealType.label}
          </span>
        )}
        <div className="flex-1" />
        {hasUrl && (
          <a
            href={deal.property_url!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={stopPointer}
            className="text-slate-500 hover:text-accent-300 text-[12px] leading-none opacity-0 group-hover:opacity-100 transition"
            title="Open listing"
          >
            ↗
          </a>
        )}
        <div className="relative">
          <button
            className="text-slate-500 hover:text-slate-200 hover:bg-white/5 w-5 h-5 rounded flex items-center justify-center text-[14px] leading-none opacity-30 group-hover:opacity-100 transition"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            onPointerDown={stopPointer}
            aria-label="More"
          >
            ⋯
          </button>
          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                }}
              />
              <div
                className="absolute right-0 top-full mt-1 z-40 bg-ink-800 border border-white/10 rounded-md shadow-xl min-w-[150px] py-1 text-xs"
                onClick={(e) => e.stopPropagation()}
              >
            {prevStage && (
              <button
                className="w-full text-left px-3 py-1.5 text-slate-300 hover:bg-white/5"
                onClick={() => {
                  setMenuOpen(false);
                  onMove(prevStage);
                }}
              >
                ← {STAGE_META[prevStage].label}
              </button>
            )}
            {nextOpen && (
              <button
                className="w-full text-left px-3 py-1.5 text-slate-300 hover:bg-white/5"
                onClick={() => {
                  setMenuOpen(false);
                  onMove(nextOpen);
                }}
              >
                → {STAGE_META[nextOpen].label}
              </button>
            )}
            {deal.stage !== "done" && deal.stage !== "lost" && (
              <>
                <div className="border-t border-white/5 my-1" />
                <button
                  className="w-full text-left px-3 py-1.5 text-emerald-300 hover:bg-emerald-500/10"
                  onClick={() => {
                    setMenuOpen(false);
                    onMove("done");
                  }}
                >
                  Mark done
                </button>
                <button
                  className="w-full text-left px-3 py-1.5 text-rose-300 hover:bg-rose-500/10"
                  onClick={() => {
                    setMenuOpen(false);
                    onMove("lost");
                  }}
                >
                  Mark lost
                </button>
              </>
            )}
            <div className="border-t border-white/5 my-1" />
            <button
              className="w-full text-left px-3 py-1.5 text-rose-400 hover:bg-rose-500/10"
              onClick={() => {
                setMenuOpen(false);
                onDelete();
              }}
            >
              Delete
            </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Title — primary identifier, two-line clamp. Bigger than before
          (13.5px) so it reads as the headline, not a label. */}
      <div className="text-[13.5px] font-medium text-slate-100 leading-snug line-clamp-2 mb-2.5">
        {deal.property_title || (
          <span className="text-slate-500 italic">(untitled)</span>
        )}
      </div>

      {/* Client row — avatar + name + click-to-call phone */}
      {deal.client && (
        <div className="flex items-center gap-2 text-[11.5px] text-slate-400 mb-3 min-w-0">
          <Avatar name={deal.client.full_name} size="xs" />
          <span className="truncate flex-1 min-w-0">{deal.client.full_name}</span>
          {deal.client.phone && (
            <a
              href={`tel:${deal.client.phone}`}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={stopPointer}
              className="text-accent-400/80 hover:text-accent-300 tabular-nums text-[11px] whitespace-nowrap flex-shrink-0"
              title="Call"
            >
              {deal.client.phone}
            </a>
          )}
        </div>
      )}

      {/* Financials block — price prominent, commission summary below */}
      <div className="mb-3">
        <div className="text-[15.5px] font-semibold text-slate-100 tabular-nums leading-none">
          {fmtMoney(deal.price, deal.currency)}
        </div>
        {commission && deal.stage !== "lost" && (
          <div className="text-[10.5px] text-slate-500 mt-1 tabular-nums">
            {deal.commission_pct ? `${deal.commission_pct}% · ` : ""}
            <span
              className={
                deal.stage === "done" ? "text-emerald-300" : "text-slate-400"
              }
            >
              {fmtMoney(commission, deal.currency)}
            </span>{" "}
            commission
          </div>
        )}
        {deal.stage === "done" && deal.deal_date && (
          <div className="text-[10.5px] text-emerald-300/70 mt-1">
            ✓ Closed {fmtDealDate(deal.deal_date)}
          </div>
        )}
      </div>

      {/* Lost reason — replaces commission summary on lost cards */}
      {deal.stage === "lost" && deal.lost_reason && (
        <div className="text-[11px] text-rose-300/80 italic line-clamp-2 mb-3">
          “{deal.lost_reason}”
        </div>
      )}

      {/* Footer — assignee on the left, age-in-stage on the right.
          Divider above hints "this row is metadata, not content". */}
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <div className="flex items-center gap-1.5 min-w-0">
          {deal.assigned_user ? (
            <>
              <Avatar
                name={deal.assigned_user.full_name}
                email={deal.assigned_user.email}
                url={deal.assigned_user.avatar_url}
                size="xs"
                online={onlineUserIds?.has(deal.assigned_user.id)}
              />
              <span
                className="text-[10.5px] text-slate-400 truncate max-w-[110px]"
                title={
                  deal.assigned_user.full_name ||
                  deal.assigned_user.email ||
                  ""
                }
              >
                {(deal.assigned_user.full_name ||
                  deal.assigned_user.email ||
                  "").split(" ")[0]}
              </span>
            </>
          ) : (
            <span className="text-[10.5px] text-slate-600">unassigned</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Departure badge — the next trip for this deal. Rose once the
              time has passed on an open deal (broker is out, or forgot to
              move the card), amber inside the hour, quiet otherwise. */}
          {deal.next_departure && (
            <span
              className={`inline-flex items-center gap-1 text-[10.5px] tabular-nums ${departureTone(
                deal.next_departure.leave_at,
                deal.stage,
              )}`}
              title={
                `Leaving ${new Date(deal.next_departure.leave_at).toLocaleString()}` +
                (deal.next_departure.user?.full_name
                  ? ` · ${deal.next_departure.user.full_name}`
                  : "") +
                (deal.next_departure.note ? ` · ${deal.next_departure.note}` : "") +
                (deal.departure_count > 1
                  ? ` · ${deal.departure_count} trips logged`
                  : "")
              }
            >
              <ClockIcon />
              {fmtDepartureWhen(deal.next_departure.leave_at)}
              {deal.departure_count > 1 && (
                <span className="text-slate-600">+{deal.departure_count - 1}</span>
              )}
            </span>
          )}
          <span
            className={`text-[10.5px] tabular-nums ${ageCls}`}
            title={`In ${STAGE_META[deal.stage].label} for ${days} day${days === 1 ? "" : "s"}`}
          >
            {ageLabel(days)}
          </span>
        </div>
      </div>
    </div>
  );
}

interface DealFormProps {
  deal: Deal | null;
  teamMembers: TeamMember[];
  clients: Client[];
  viewerRole: "executive_manager" | "operations_manager" | "lead_broker" | "broker" | "member";
  /** Optional client id to preselect when creating a new deal (edit
   *  mode ignores it — the existing deal's ``client_id`` wins). Passed
   *  in from ``?newClient=<id>`` on the URL. */
  initialClientId?: string | null;
  /** A client created from inside the picker — hoisted so the page's own
   *  list carries it for the rest of the session. */
  onClientCreated?: (c: Client) => void;
  /** Called after a departure is logged, edited or removed. Those writes
   *  land immediately (they're not part of the Save-changes payload), so
   *  the page reloads the board to keep the card badge honest even while
   *  the modal is still open — otherwise a broker who logs a time and hits
   *  Cancel sees a card with no clock on it. */
  onDeparturesChanged?: () => void | Promise<void>;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

/** Borderless "value-looking" input class for the Edit-Deal inspector.
 *  Hovering shows a subtle bg so the row reads as interactive; focusing
 *  adds a 1px ring + the input's own surface so the active field is
 *  unambiguous. Pairs with ``inspectorSelectCls`` so selects line up
 *  with text inputs at the same height. */
const inspectorInputCls =
  "w-full bg-transparent border-0 px-2 py-1.5 rounded text-[12.5px] text-slate-100 placeholder:text-slate-600 hover:bg-white/[0.03] focus:bg-ink-850 focus:outline-none focus:ring-1 focus:ring-white/15 transition";
const inspectorSelectCls =
  inspectorInputCls + " appearance-none cursor-pointer";

/** One inspector row — small uppercase label on the left, editable
 *  value on the right, optional small hint text underneath. Hover dim-
 *  highlights the whole row so the label + value read as a single unit
 *  rather than "label" + "form field". */
function InspectorRow({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[100px_1fr] items-start gap-3 py-1.5 -mx-2 px-2 rounded hover:bg-white/[0.015] transition">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-2 leading-snug">
        {label}
      </div>
      <div className="min-w-0">
        {children}
        {hint && (
          <div className="text-[10.5px] text-slate-500 mt-0.5 px-2">{hint}</div>
        )}
      </div>
    </div>
  );
}

function InspectorReadValue({
  children,
  mono,
}: {
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <div
      className={
        "px-2 py-1.5 text-[12.5px] text-slate-300 " +
        (mono ? "tabular-nums" : "")
      }
    >
      {children}
    </div>
  );
}

function DealFormModal({
  deal,
  teamMembers,
  clients,
  viewerRole,
  initialClientId,
  onClientCreated,
  onDeparturesChanged,
  onClose,
  onSaved,
}: DealFormProps) {
  const { user } = useAuth();
  const isEdit = deal !== null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [assignedUserId, setAssignedUserId] = useState(
    deal?.assigned_user_id ?? "",
  );
  const [clientId, setClientId] = useState(
    deal?.client_id ?? initialClientId ?? "",
  );
  const [stage, setStage] = useState<DealStage>(deal?.stage ?? "planning");
  const [dealType, setDealType] = useState<"" | "sale" | "rent">(
    deal?.deal_type ?? "",
  );
  const [propertyTitle, setPropertyTitle] = useState(deal?.property_title ?? "");
  const [propertyUrl, setPropertyUrl] = useState(deal?.property_url ?? "");
  const [price, setPrice] = useState(deal?.price ?? "");
  const [currency, setCurrency] = useState<"" | "USD" | "GEL" | "EUR">(
    (deal?.currency as any) ?? "USD",
  );
  const [commissionPct, setCommissionPct] = useState(deal?.commission_pct ?? "");
  const [commissionAmount, setCommissionAmount] = useState(
    deal?.commission_amount ?? "",
  );
  // Seller's phone. Distinct from ``deal.client?.phone`` (the buyer);
  // this is the contact for the property owner the broker is dealing
  // with on viewings / negotiation. Pre-filled from the source HotOffer
  // server-side when present; manual otherwise.
  const [ownerPhone, setOwnerPhone] = useState(deal?.owner_phone ?? "");
  const [lostReason, setLostReason] = useState(deal?.lost_reason ?? "");
  const [notes, setNotes] = useState(deal?.notes ?? "");
  // Default new deals to today (broker's local date — matches the typical
  // "logging today's deal" case). Edit mode preserves the stored value.
  const [dealDate, setDealDate] = useState(
    deal?.deal_date ?? new Date().toISOString().slice(0, 10),
  );

  // Activity stream — chronological updates posted on this deal.
  // Loaded once on mount for an existing deal; new deals don't have an
  // id yet so the section stays hidden until first save.
  const [comments, setComments] = useState<DealComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsErr, setCommentsErr] = useState<string | null>(null);
  const [newCommentBody, setNewCommentBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  // Departures — the trips logged for this deal. They share the activity
  // section with comments (one merged stream, newest entry first) because
  // both answer "what has happened on this deal"; a separate list would
  // make the reader interleave two timelines by hand.
  const [departures, setDepartures] = useState<DealDeparture[]>([]);
  // The composer defaults to today + an empty time: the overwhelmingly
  // common entry is "leaving today at <time>", so the broker types four
  // digits and hits Add.
  const [depDate, setDepDate] = useState(() => localDateKey(new Date()));
  const [depTime, setDepTime] = useState("");
  const [depNote, setDepNote] = useState("");
  const [addingDeparture, setAddingDeparture] = useState(false);
  const [deletingDepartureId, setDeletingDepartureId] = useState<string | null>(
    null,
  );
  // Inline edit of one logged trip — a mistyped time is the whole reason
  // the PATCH endpoint exists, and delete-and-retype loses the note.
  const [editingDepId, setEditingDepId] = useState<string | null>(null);
  const [editDepDate, setEditDepDate] = useState("");
  const [editDepTime, setEditDepTime] = useState("");
  const [editDepNote, setEditDepNote] = useState("");
  const [savingDeparture, setSavingDeparture] = useState(false);

  const showBrokerSelect =
    viewerRole === "executive_manager" || viewerRole === "lead_broker";
  const isManagerPlus =
    viewerRole === "executive_manager" ||
    viewerRole === "operations_manager" ||
    viewerRole === "lead_broker";

  // Connected price ↔ commission% ↔ commission-amount math. Whichever
  // field the broker just touched is treated as the source of truth and
  // the dependent field is recomputed live, so the three numbers stay
  // consistent without a "recalculate" button. ``round2`` keeps the
  // rendered values tidy (3.5% · 1,750 USD, not 3.49999… · 1750.0000).
  function round2(n: number): string {
    return String(Math.round(n * 100) / 100);
  }
  function onPriceChange(v: string) {
    setPrice(v);
    const p = Number(v);
    if (!Number.isFinite(p) || p <= 0) return;
    const c = Number(commissionPct);
    if (Number.isFinite(c) && c > 0) {
      setCommissionAmount(round2((p * c) / 100));
      return;
    }
    const a = Number(commissionAmount);
    if (Number.isFinite(a) && a > 0) {
      setCommissionPct(round2((a / p) * 100));
    }
  }
  function onPctChange(v: string) {
    setCommissionPct(v);
    const p = Number(price);
    const c = Number(v);
    if (Number.isFinite(p) && p > 0 && Number.isFinite(c) && c > 0) {
      setCommissionAmount(round2((p * c) / 100));
    }
  }
  function onAmountChange(v: string) {
    setCommissionAmount(v);
    const p = Number(price);
    const a = Number(v);
    if (Number.isFinite(p) && p > 0 && Number.isFinite(a) && a > 0) {
      setCommissionPct(round2((a / p) * 100));
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Pre-flight checks for the create path. Edit mode keeps PATCH semantics
    // (any subset is fine) so an existing deal can be tweaked field-by-field.
    if (!isEdit) {
      const trimmedTitle = propertyTitle.trim();
      const trimmedUrl = propertyUrl.trim();
      if (
        !dealType ||
        !trimmedTitle ||
        !trimmedUrl ||
        !price ||
        Number(price) <= 0 ||
        !currency ||
        !dealDate
      ) {
        setErr("Please fill in every required field before saving.");
        return;
      }
      if (!commissionPct && !commissionAmount) {
        setErr("Commission % or commission amount is required.");
        return;
      }
    }
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {
        stage,
        deal_type: dealType || null,
        deal_date: dealDate,
        property_title: propertyTitle.trim() || null,
        property_url: propertyUrl.trim() || null,
        price: price ? Number(price) : null,
        currency: currency || null,
        owner_phone: ownerPhone.trim() || null,
        commission_pct: commissionPct ? Number(commissionPct) : null,
        commission_amount: commissionAmount ? Number(commissionAmount) : null,
        notes: notes.trim() || null,
      };
      if (stage === "lost") body.lost_reason = lostReason.trim() || null;
      body.client_id = clientId || null;
      if (showBrokerSelect) body.assigned_user_id = assignedUserId || null;

      if (isEdit) {
        await api(`/api/deals/${deal!.id}`, { method: "PATCH", json: body });
      } else {
        await api("/api/deals", { method: "POST", json: body });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  // Load the activity stream when the modal opens for an existing deal.
  // New deals (no id yet) skip this — the section is hidden until first
  // save anyway. ``cancelled`` guards against a fast close-reopen race.
  useEffect(() => {
    if (!isEdit || !deal) return;
    let cancelled = false;
    setCommentsLoading(true);
    setCommentsErr(null);
    Promise.all([
      api<DealCommentListResponse>(`/api/deals/${deal.id}/comments`),
      api<DealDepartureListResponse>(`/api/deals/${deal.id}/departures`),
    ])
      .then(([c, dep]) => {
        if (cancelled) return;
        setComments(c.items);
        setDepartures(dep.items);
      })
      .catch((e) => {
        if (!cancelled) {
          setCommentsErr(
            e instanceof ApiError ? e.message : "Failed to load activity",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, deal?.id]);

  async function postComment() {
    const text = newCommentBody.trim();
    if (!text || !deal) return;
    setPosting(true);
    setCommentsErr(null);
    try {
      const c = await api<DealComment>(`/api/deals/${deal.id}/comments`, {
        method: "POST",
        json: { body: text },
      });
      // Optimistic prepend — newest-first matches the backend's order.
      setComments((prev) => [c, ...prev]);
      setNewCommentBody("");
    } catch (e) {
      setCommentsErr(e instanceof ApiError ? e.message : "Post failed");
    } finally {
      setPosting(false);
    }
  }

  async function deleteComment(commentId: string) {
    if (!deal) return;
    setDeletingCommentId(commentId);
    setCommentsErr(null);
    try {
      await api(`/api/deals/${deal.id}/comments/${commentId}`, {
        method: "DELETE",
      });
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (e) {
      setCommentsErr(e instanceof ApiError ? e.message : "Delete failed");
    } finally {
      setDeletingCommentId(null);
    }
  }

  /** Newest-first by appointment time, matching what the list endpoint
   *  returns — so an optimistic insert lands where a reload would put it. */
  function sortDepartures(rows: DealDeparture[]): DealDeparture[] {
    return [...rows].sort(
      (a, b) => new Date(b.leave_at).getTime() - new Date(a.leave_at).getTime(),
    );
  }

  async function addDeparture() {
    const leaveAt = buildLeaveAt(depDate, depTime);
    if (!leaveAt || !deal) return;
    setAddingDeparture(true);
    setCommentsErr(null);
    try {
      const created = await api<DealDeparture>(
        `/api/deals/${deal.id}/departures`,
        { method: "POST", json: { leave_at: leaveAt, note: depNote.trim() || null } },
      );
      setDepartures((prev) => sortDepartures([created, ...prev]));
      // Keep the date (a broker logging two trips usually means the same
      // day) and clear the parts that are specific to the one just added.
      setDepTime("");
      setDepNote("");
      await onDeparturesChanged?.();
    } catch (e) {
      setCommentsErr(e instanceof ApiError ? e.message : "Could not log the time");
    } finally {
      setAddingDeparture(false);
    }
  }

  function startEditDeparture(dep: DealDeparture) {
    const parts = departureInputParts(dep.leave_at);
    setEditingDepId(dep.id);
    setEditDepDate(parts.date);
    setEditDepTime(parts.time);
    setEditDepNote(dep.note ?? "");
  }

  async function saveDeparture() {
    const leaveAt = buildLeaveAt(editDepDate, editDepTime);
    if (!leaveAt || !deal || !editingDepId) return;
    setSavingDeparture(true);
    setCommentsErr(null);
    try {
      const updated = await api<DealDeparture>(
        `/api/deals/${deal.id}/departures/${editingDepId}`,
        {
          method: "PATCH",
          json: { leave_at: leaveAt, note: editDepNote.trim() || null },
        },
      );
      setDepartures((prev) =>
        sortDepartures(prev.map((d) => (d.id === updated.id ? updated : d))),
      );
      setEditingDepId(null);
      await onDeparturesChanged?.();
    } catch (e) {
      setCommentsErr(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSavingDeparture(false);
    }
  }

  async function deleteDeparture(departureId: string) {
    if (!deal) return;
    setDeletingDepartureId(departureId);
    setCommentsErr(null);
    try {
      await api(`/api/deals/${deal.id}/departures/${departureId}`, {
        method: "DELETE",
      });
      setDepartures((prev) => prev.filter((d) => d.id !== departureId));
      await onDeparturesChanged?.();
    } catch (e) {
      setCommentsErr(e instanceof ApiError ? e.message : "Delete failed");
    } finally {
      setDeletingDepartureId(null);
    }
  }

  // Reporter for the sidebar. On edit, the persisted ``created_by``. On
  // create, preview the viewer so the broker knows whose name will land
  // on the row before they hit save.
  const reporterName = isEdit
    ? deal?.created_by?.full_name || deal?.created_by?.email || "—"
    : user?.full_name || user?.email || "—";
  const reporterAvatarUrl = isEdit
    ? deal?.created_by?.avatar_url ?? null
    : user?.avatar_url ?? null;

  // Stage timeline — only the stages the deal has actually touched
  // (timestamps present), in order. The current stage is flagged so the
  // sidebar reads as a small "you are here" path.
  const stageEvents = (
    isEdit && deal
      ? [
          { stage: "planning" as DealStage, at: deal.planning_at },
          { stage: "booked" as DealStage, at: deal.booked_at },
          { stage: "decision_pending" as DealStage, at: deal.decision_pending_at },
          { stage: "done" as DealStage, at: deal.done_at },
          { stage: "lost" as DealStage, at: deal.lost_at },
        ]
      : []
  ).filter((e) => !!e.at);

  // Commission summary — only renders when all three numbers are
  // resolvable, so an in-progress edit doesn't flash "NaN".
  const priceN = Number(price);
  const pctN = Number(commissionPct);
  const amtN = Number(commissionAmount);
  const commResolved =
    Number.isFinite(priceN) &&
    priceN > 0 &&
    Number.isFinite(pctN) &&
    pctN > 0 &&
    Number.isFinite(amtN) &&
    amtN > 0;

  // One merged activity stream: comments and logged trips together, newest
  // entry first. Sorted on ``created_at`` (when someone made the entry),
  // not on ``leave_at`` — this is a log of what people did, and a trip
  // booked for next Thursday was still logged this afternoon.
  type StreamEntry =
    | { kind: "comment"; at: string; comment: DealComment }
    | { kind: "departure"; at: string; departure: DealDeparture };
  const stream: StreamEntry[] = [
    ...comments.map<StreamEntry>((c) => ({
      kind: "comment",
      at: c.created_at,
      comment: c,
    })),
    ...departures.map<StreamEntry>((d) => ({
      kind: "departure",
      at: d.created_at,
      departure: d,
    })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="card w-full max-w-2xl flex flex-col max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] shadow-2xl"
        onSubmit={submit}
      >
        {/* Header — title + short id + stage chip + close. Same content
            as before, just narrower since we collapsed the side-pane. */}
        <div className="px-5 sm:px-6 pt-4 pb-3 border-b border-white/5 flex-shrink-0 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <h2 className="card-title leading-tight">
                {isEdit ? "Edit deal" : "New deal"}
              </h2>
              {isEdit && deal && (
                <span className="font-mono text-[10.5px] text-slate-500 tabular-nums">
                  #{deal.id.slice(0, 8)}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-[2px] rounded border border-white/10 bg-white/[0.03]">
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${STAGE_META[stage].dot}`}
                />
                <span className={STAGE_META[stage].accent}>
                  {STAGE_META[stage].label}
                </span>
              </span>
            </div>
            {isEdit && deal?.property_title && (
              <div className="text-[11.5px] text-slate-500 truncate">
                {deal.property_title}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 px-1.5 py-1 rounded hover:bg-white/5"
            aria-label="Close"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body — one scroll area, single-column inspector + activity. No
            sidebar; the previously side-pane fields (Reporter, Assignee,
            Client, dates, stage log) are inline rows in the same grid as
            the editable fields. Reads as a property panel, not a form. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-5 sm:px-6 py-3">
            <InspectorRow label="Stage">
              <select
                className={inspectorSelectCls}
                value={stage}
                onChange={(e) => setStage(e.target.value as DealStage)}
              >
                {DEAL_STAGES.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_META[s].label}
                  </option>
                ))}
              </select>
            </InspectorRow>
            <InspectorRow label="Type">
              <select
                className={inspectorSelectCls}
                value={dealType}
                onChange={(e) => setDealType(e.target.value as any)}
                required={!isEdit}
              >
                <option value="">—</option>
                <option value="sale">Sale</option>
                <option value="rent">Rent</option>
              </select>
            </InspectorRow>

            <InspectorRow label="Reporter">
              <div className="flex items-center gap-2 px-2 py-1">
                <Avatar size="xs" name={reporterName} url={reporterAvatarUrl} />
                <span className="text-[12.5px] text-slate-200 truncate">
                  {reporterName}
                </span>
                {!isEdit && (
                  <span className="text-[10px] text-slate-600 ml-1">
                    (you, on save)
                  </span>
                )}
              </div>
            </InspectorRow>

            {showBrokerSelect ? (
              <InspectorRow label="Assignee">
                <UserSelect
                  value={assignedUserId}
                  onChange={setAssignedUserId}
                  users={teamMembers}
                  viewerId={user?.id}
                  noneLabel="— unassigned —"
                  noneAvatar
                  triggerClass="inline-flex items-center gap-2 w-full h-9 pl-2 pr-2.5 rounded-lg bg-ink-850 border border-white/10 text-[12px] text-slate-200 hover:border-white/20 cursor-pointer focus:outline-none focus:border-accent-500/60"
                />
              </InspectorRow>
            ) : (
              isEdit &&
              deal?.assigned_user && (
                <InspectorRow label="Assignee">
                  <div className="flex items-center gap-2 px-2 py-1">
                    <Avatar
                      size="xs"
                      name={deal.assigned_user.full_name}
                      email={deal.assigned_user.email}
                      url={deal.assigned_user.avatar_url}
                    />
                    <span className="text-[12.5px] text-slate-200 truncate">
                      {deal.assigned_user.full_name || deal.assigned_user.email}
                    </span>
                  </div>
                </InspectorRow>
              )
            )}

            <InspectorRow label="Client">
              <ClientSelect
                value={clientId}
                onChange={setClientId}
                clients={clients}
                fallback={deal?.client ?? null}
                onClientCreated={onClientCreated}
              />
            </InspectorRow>

            <InspectorRow label="Owner phone">
              <div className="relative">
                <input
                  className={inspectorInputCls + " tabular-nums pr-14"}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  value={ownerPhone}
                  onChange={(e) => setOwnerPhone(e.target.value)}
                  placeholder="+995 555 ..."
                  maxLength={32}
                />
                {ownerPhone.trim() && (
                  <a
                    href={`tel:${ownerPhone.trim()}`}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-400 hover:text-accent-300 text-[10.5px]"
                    title="Call"
                  >
                    call ↗
                  </a>
                )}
              </div>
            </InspectorRow>

            <InspectorRow label="Title">
              <input
                className={inspectorInputCls}
                value={propertyTitle}
                onChange={(e) => setPropertyTitle(e.target.value)}
                placeholder="3br apartment · Saburtalo"
                required={!isEdit}
              />
            </InspectorRow>

            <InspectorRow label="Listing URL">
              <div className="relative">
                <input
                  className={inspectorInputCls + " pr-14"}
                  value={propertyUrl}
                  onChange={(e) => setPropertyUrl(e.target.value)}
                  placeholder="https://www.myhome.ge/pr/…"
                  required={!isEdit}
                />
                {propertyUrl && (
                  <a
                    href={propertyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-400 hover:text-accent-300 text-[10.5px]"
                    title="Open in a new tab"
                  >
                    open ↗
                  </a>
                )}
              </div>
            </InspectorRow>

            <InspectorRow label="Price">
              <div className="flex gap-2">
                <input
                  className={inspectorInputCls + " tabular-nums"}
                  type="number"
                  min={0}
                  value={price}
                  onChange={(e) => onPriceChange(e.target.value)}
                  placeholder="0"
                  required={!isEdit}
                />
                <select
                  className={inspectorSelectCls + " !w-20 flex-shrink-0"}
                  value={currency}
                  onChange={(e) =>
                    setCurrency(e.target.value as "" | "USD" | "GEL" | "EUR")
                  }
                  required={!isEdit}
                >
                  <option value="">—</option>
                  <option value="USD">USD</option>
                  <option value="GEL">GEL</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </InspectorRow>

            <InspectorRow
              label="Commission"
              hint={
                commResolved ? (
                  <>
                    {pctN}% of {fmtMoneyFull(priceN, currency || null)} ={" "}
                    <span className="text-slate-300">
                      {fmtMoneyFull(amtN, currency || null)}
                    </span>
                  </>
                ) : !isEdit && !commissionPct && !commissionAmount ? (
                  "Enter % or amount — the other fills in automatically."
                ) : null
              }
            >
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <input
                    className={inspectorInputCls + " tabular-nums pr-6"}
                    type="number"
                    min={0}
                    max={100}
                    // 0.01 matches the backend ``Numeric(5, 2)`` column AND
                    // the ``round2`` helper's precision on the auto-computed
                    // counterpart, so "33.33%" enters cleanly without the
                    // browser's "nearest values are 33.3 / 33.4" rejection.
                    step="0.01"
                    value={commissionPct}
                    onChange={(e) => onPctChange(e.target.value)}
                    placeholder="3.33"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-500 pointer-events-none">
                    %
                  </span>
                </div>
                <span className="text-slate-600 text-xs">·</span>
                <div className="relative flex-1">
                  <input
                    className={inspectorInputCls + " tabular-nums pr-10"}
                    type="number"
                    min={0}
                    // Same 2-decimal precision as the backend ``Numeric(12, 2)``
                    // column — without ``step``, the input defaults to 1 and
                    // browsers reject "99.99" with the same nearest-values
                    // complaint that hit the pct field.
                    step="0.01"
                    value={commissionAmount}
                    onChange={(e) => onAmountChange(e.target.value)}
                    placeholder="amount"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10.5px] text-slate-500 pointer-events-none">
                    {currency || "—"}
                  </span>
                </div>
              </div>
            </InspectorRow>

            <InspectorRow label="Deal date">
              <input
                className={inspectorInputCls + " tabular-nums"}
                type="date"
                value={dealDate}
                onChange={(e) => setDealDate(e.target.value)}
                required
                max="2999-12-31"
              />
            </InspectorRow>

            {stage === "lost" && (
              <InspectorRow label="Lost reason">
                <input
                  className={inspectorInputCls}
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  placeholder="Price, timing, competitor…"
                />
              </InspectorRow>
            )}

            <InspectorRow label="Notes">
              <textarea
                className={inspectorInputCls + " min-h-[64px] resize-y"}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Curated context — broker observations, client preferences, deal terms."
              />
            </InspectorRow>

            {isEdit && deal && (
              <>
                <InspectorRow label="Created">
                  <InspectorReadValue mono>
                    <span title={new Date(deal.created_at).toLocaleString()}>
                      {fmtRelativeAgo(deal.created_at)}
                    </span>
                  </InspectorReadValue>
                </InspectorRow>
                <InspectorRow label="Updated">
                  <InspectorReadValue mono>
                    <span title={new Date(deal.updated_at).toLocaleString()}>
                      {fmtRelativeAgo(deal.updated_at)}
                    </span>
                  </InspectorReadValue>
                </InspectorRow>
                {stageEvents.length > 0 && (
                  <InspectorRow label="Stage log">
                    <ol className="px-2 py-1 space-y-1">
                      {stageEvents.map((ev) => {
                        const m = STAGE_META[ev.stage];
                        const isCurrent = ev.stage === deal.stage;
                        return (
                          <li
                            key={ev.stage}
                            className="flex items-center gap-2"
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${m.dot}`}
                            />
                            <span
                              className={`${m.accent} text-[11px] flex-shrink-0 ${
                                isCurrent ? "font-semibold" : ""
                              }`}
                            >
                              {m.label}
                            </span>
                            <span
                              className="text-slate-600 flex-1 text-right text-[10.5px] tabular-nums"
                              title={new Date(ev.at!).toLocaleString()}
                            >
                              {fmtRelativeAgo(ev.at!)}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  </InspectorRow>
                )}
              </>
            )}
          </div>

          {/* ── Activity stream ─────────────────────────────────────
              Visible only when editing an existing deal — a new deal
              has no id yet, so there's nothing to comment on. The
              composer accepts ⌘/Ctrl-Enter as a shortcut for Post. */}
          {isEdit && (
            <section className="border-t border-white/5 px-5 sm:px-6 py-4">
              <div className="flex items-baseline justify-between mb-2.5">
                <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Activity
                  {!commentsLoading && (
                    <span className="ml-1.5 text-slate-400 tabular-nums">
                      ({stream.length})
                    </span>
                  )}
                </h3>
                {commentsLoading && (
                  <span className="text-[10.5px] text-slate-600">loading…</span>
                )}
              </div>

              {/* Departure composer — "I'm leaving the office at 15:00".
                  Time first because that's the field the broker actually
                  types; the date sits next to it pre-filled to today and
                  only gets touched when booking ahead. Enter submits the
                  row rather than the whole deal form. */}
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-white/8 bg-ink-900/40 px-2.5 py-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  <ClockIcon />
                  Leaving office
                </span>
                <input
                  type="time"
                  value={depTime}
                  onChange={(e) => setDepTime(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addDeparture();
                    }
                  }}
                  className="h-7 bg-ink-850 border border-white/10 rounded px-2 text-[12px] text-slate-100 tabular-nums focus:outline-none focus:border-accent-500/60"
                  aria-label="Departure time"
                />
                <input
                  type="date"
                  value={depDate}
                  onChange={(e) => setDepDate(e.target.value)}
                  max="2999-12-31"
                  className="h-7 bg-ink-850 border border-white/10 rounded px-2 text-[12px] text-slate-100 tabular-nums focus:outline-none focus:border-accent-500/60"
                  aria-label="Departure date"
                />
                <input
                  value={depNote}
                  onChange={(e) => setDepNote(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addDeparture();
                    }
                  }}
                  placeholder="note — client meets us there…"
                  maxLength={500}
                  className="h-7 flex-1 min-w-[140px] bg-ink-850 border border-white/10 rounded px-2 text-[12px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-accent-500/60"
                  aria-label="Departure note"
                />
                <button
                  type="button"
                  onClick={addDeparture}
                  disabled={!depTime || addingDeparture}
                  className="inline-flex items-center h-7 px-3 rounded-md text-[11.5px] font-medium bg-white/[0.06] hover:bg-white/[0.12] text-slate-200 border border-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {addingDeparture ? "Adding…" : "Add time"}
                </button>
              </div>

              <div className="mb-3.5">
                <textarea
                  className={inspectorInputCls + " min-h-[56px] resize-y"}
                  value={newCommentBody}
                  onChange={(e) => setNewCommentBody(e.target.value)}
                  placeholder="Add an update — viewing scheduled, owner response, counter-offer, follow-up reminder…"
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      (e.metaKey || e.ctrlKey) &&
                      newCommentBody.trim()
                    ) {
                      e.preventDefault();
                      postComment();
                    }
                  }}
                />
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-[10px] text-slate-600">
                    ⌘ / Ctrl + Enter to post
                  </span>
                  <div className="flex-1" />
                  <button
                    type="button"
                    className="inline-flex items-center h-7 px-3 rounded-md text-[11.5px] font-medium bg-accent-600 hover:bg-accent-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={postComment}
                    disabled={posting || !newCommentBody.trim()}
                  >
                    {posting ? "Posting…" : "Post update"}
                  </button>
                </div>
              </div>

              {commentsErr && (
                <div className="mb-2 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded p-2">
                  {commentsErr}
                </div>
              )}

              {stream.length === 0 && !commentsLoading && !commentsErr && (
                <div className="text-[11px] text-slate-600 text-center py-4 border border-dashed border-white/8 rounded">
                  Nothing logged yet. Add a departure time or post an update.
                </div>
              )}
              <ul className="space-y-3">
                {stream.map((entry) => {
                  if (entry.kind === "comment") {
                    const c = entry.comment;
                    const canDelete =
                      (user && c.user_id === user.id) || isManagerPlus;
                    const authorName =
                      c.author?.full_name || c.author?.email || "—";
                    return (
                      <li key={`c:${c.id}`} className="flex gap-2.5">
                        <Avatar
                          size="xs"
                          name={authorName}
                          url={c.author?.avatar_url ?? null}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-[11.5px] font-medium text-slate-200 truncate">
                              {authorName}
                            </span>
                            <span
                              className="text-[10px] text-slate-500 tabular-nums"
                              title={new Date(c.created_at).toLocaleString()}
                            >
                              {fmtRelativeAgo(c.created_at)}
                            </span>
                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => deleteComment(c.id)}
                                disabled={deletingCommentId === c.id}
                                className="ml-auto text-[10px] text-slate-600 hover:text-rose-300 disabled:opacity-50 transition-colors"
                                title="Delete update"
                              >
                                {deletingCommentId === c.id ? "…" : "delete"}
                              </button>
                            )}
                          </div>
                          <div className="text-[12px] text-slate-300 whitespace-pre-wrap leading-snug mt-0.5">
                            {c.body}
                          </div>
                        </div>
                      </li>
                    );
                  }

                  // ── a logged trip ──────────────────────────────────────
                  const dep = entry.departure;
                  const canEdit =
                    (user && dep.user_id === user.id) || isManagerPlus;
                  const who = dep.user?.full_name || dep.user?.email || "—";
                  const isEditingThis = editingDepId === dep.id;
                  return (
                    <li key={`d:${dep.id}`} className="flex gap-2.5">
                      <Avatar
                        size="xs"
                        name={dep.user?.full_name ?? null}
                        email={dep.user?.email ?? null}
                        url={dep.user?.avatar_url ?? null}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[11.5px] font-medium text-slate-200 truncate">
                            {who}
                          </span>
                          <span
                            className="text-[10px] text-slate-500 tabular-nums"
                            title={new Date(dep.created_at).toLocaleString()}
                          >
                            {fmtRelativeAgo(dep.created_at)}
                          </span>
                          {canEdit && !isEditingThis && (
                            <span className="ml-auto flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => startEditDeparture(dep)}
                                className="text-[10px] text-slate-600 hover:text-slate-300 transition-colors"
                                title="Change the time"
                              >
                                edit
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteDeparture(dep.id)}
                                disabled={deletingDepartureId === dep.id}
                                className="text-[10px] text-slate-600 hover:text-rose-300 disabled:opacity-50 transition-colors"
                                title="Remove this trip"
                              >
                                {deletingDepartureId === dep.id ? "…" : "delete"}
                              </button>
                            </span>
                          )}
                        </div>

                        {isEditingThis ? (
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <input
                              type="time"
                              value={editDepTime}
                              onChange={(e) => setEditDepTime(e.target.value)}
                              className="h-7 bg-ink-850 border border-white/10 rounded px-2 text-[12px] text-slate-100 tabular-nums focus:outline-none focus:border-accent-500/60"
                              aria-label="Departure time"
                            />
                            <input
                              type="date"
                              value={editDepDate}
                              onChange={(e) => setEditDepDate(e.target.value)}
                              max="2999-12-31"
                              className="h-7 bg-ink-850 border border-white/10 rounded px-2 text-[12px] text-slate-100 tabular-nums focus:outline-none focus:border-accent-500/60"
                              aria-label="Departure date"
                            />
                            <input
                              value={editDepNote}
                              onChange={(e) => setEditDepNote(e.target.value)}
                              placeholder="note"
                              maxLength={500}
                              className="h-7 flex-1 min-w-[120px] bg-ink-850 border border-white/10 rounded px-2 text-[12px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-accent-500/60"
                              aria-label="Departure note"
                            />
                            <button
                              type="button"
                              onClick={saveDeparture}
                              disabled={savingDeparture || !editDepTime}
                              className="inline-flex items-center h-7 px-2.5 rounded-md text-[11px] font-medium bg-accent-600 hover:bg-accent-500 text-white disabled:opacity-40 transition-colors"
                            >
                              {savingDeparture ? "…" : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingDepId(null)}
                              className="text-[10.5px] text-slate-500 hover:text-slate-300"
                            >
                              cancel
                            </button>
                          </div>
                        ) : (
                          <div className="text-[12px] leading-snug mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span
                              className={`inline-flex items-center gap-1 tabular-nums font-medium ${departureTone(
                                dep.leave_at,
                                stage,
                              )}`}
                              title={new Date(dep.leave_at).toLocaleString()}
                            >
                              <ClockIcon />
                              Leaving {fmtDepartureWhen(dep.leave_at)}
                            </span>
                            {dep.note && (
                              <>
                                <span className="text-slate-700">·</span>
                                <span className="text-slate-400">{dep.note}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {err && (
            <div className="mx-5 sm:mx-6 mb-4 text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3 border-t border-white/5 flex-shrink-0 flex items-center gap-2">
          <div className="flex-1" />
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create deal"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Compact relative-time formatter used by the modal sidebar. Renders
 *  "just now / 5 min ago / 2 hours ago / 3 days ago / May 13". Hover
 *  shows the absolute timestamp via the calling site's ``title``. */
function fmtRelativeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const diffMs = Date.now() - then;
  if (diffMs < 60_000) return "just now";
  const m = Math.floor(diffMs / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

// ─── Manager+ filters & stats ───────────────────────────────────────────


function describeTimeRange(v: TimeRangeValue): string {
  if (v.mode === "1d") return "last 24h";
  if (v.mode === "7d") return "last 7 days";
  if (v.mode === "30d") return "last 30 days";
  if (v.mode === "this_month") return "this month";
  if (v.mode === "last_month") return "last month";
  if (v.mode === "custom") {
    if (v.from && v.to) return `${v.from} → ${v.to}`;
    if (v.from) return `since ${v.from}`;
    if (v.to) return `up to ${v.to}`;
    return "custom range";
  }
  return "all time";
}

/** Popover user-picker — replaces the previous native ``<select>`` so
 *  options can carry avatars + role chips. Used for both the toolbar
 *  "filter by assigned broker" chip and the inspector modal's
 *  Assignee row. ``noneLabel`` is shown as the first option (e.g.
 *  "Everyone" for the filter, "— unassigned —" for the inspector);
 *  set ``noneAvatar={false}`` if you don't want a placeholder avatar
 *  rendered against the empty pick.
 *
 *  Trigger button matches the filter-pill spec used elsewhere on the
 *  page (h-9 rounded-lg bg-ink-900/60 border-white/10) so the toolbar
 *  reads as one rhythm. Backend already enforces visibility (manager
 *  can't pick a user outside their reports), so the option list is
 *  trusted from /api/team. */
function UserSelect({
  value,
  onChange,
  users,
  viewerId,
  noneLabel,
  noneAvatar = false,
  triggerClass,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  users: TeamMember[];
  viewerId: string | undefined;
  /** Label for the "no selection" row at the top of the list. */
  noneLabel: string;
  /** Whether the empty pick should render a placeholder avatar slot
   *  (kept off for filters where the trigger reads "Everyone" and a
   *  blank avatar circle would be noisy). */
  noneAvatar?: boolean;
  triggerClass?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Sort: viewer first (most-used pick), then alphabetically.
  const sorted = useMemo(() => {
    const copy = [...users];
    copy.sort((a, b) => {
      if (a.id === viewerId) return -1;
      if (b.id === viewerId) return 1;
      const an = (a.full_name || a.email || "").toLowerCase();
      const bn = (b.full_name || b.email || "").toLowerCase();
      return an.localeCompare(bn);
    });
    return copy;
  }, [users, viewerId]);

  const selected = value ? users.find((u) => u.id === value) ?? null : null;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={selected ? selected.full_name || selected.email || "" : noneLabel}
        className={
          triggerClass ??
          "inline-flex items-center gap-2 h-9 pl-2 pr-2.5 rounded-lg bg-ink-900/60 border border-white/10 text-[12px] font-medium text-slate-300 hover:border-white/20 hover:text-slate-100 cursor-pointer focus:outline-none focus:border-white/25 transition-colors max-w-[220px]"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <>
            <Avatar
              size="xs"
              name={selected.full_name}
              email={selected.email}
              url={selected.avatar_url}
            />
            <span className="truncate">
              {(selected.full_name || selected.email) +
                (selected.id === viewerId ? " (you)" : "")}
            </span>
          </>
        ) : (
          <span className="truncate text-slate-400">
            {placeholder ?? noneLabel}
          </span>
        )}
        <svg
          className="w-2.5 h-2.5 text-slate-500 shrink-0 ml-auto"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-[calc(100%+4px)] z-40 min-w-[240px] max-h-[320px] overflow-y-auto rounded-lg bg-ink-900 border border-white/10 shadow-card py-1"
        >
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-left hover:bg-white/[0.05] ${
              value === "" ? "text-accent-300" : "text-slate-300"
            }`}
            role="option"
            aria-selected={value === ""}
          >
            {noneAvatar && <span className="w-5 h-5 shrink-0" />}
            <span className="flex-1 truncate">{noneLabel}</span>
            {value === "" && <span className="text-accent-400 text-xs">✓</span>}
          </button>
          {sorted.length > 0 && (
            <div className="border-t border-white/5 my-1" />
          )}
          {sorted.map((m) => {
            const isSel = m.id === value;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-left hover:bg-white/[0.05] ${
                  isSel ? "bg-white/[0.04]" : ""
                }`}
                role="option"
                aria-selected={isSel}
              >
                <Avatar
                  size="xs"
                  name={m.full_name}
                  email={m.email}
                  url={m.avatar_url}
                />
                <span className="flex-1 truncate text-slate-200">
                  {(m.full_name || m.email) +
                    (m.id === viewerId ? " (you)" : "")}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">
                  {roleLabel(m.role)}
                </span>
                {isSel && (
                  <span className="text-accent-400 text-xs">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Toolbar "Sale / Rent / All" filter. Segmented pill — 3 options is
 *  too few to justify a dropdown, and inline buttons keep the active
 *  pick visible without a click. Sits between the time range and the
 *  broker picker in the toolbar order. */
function DealTypeFilter({
  value,
  onChange,
}: {
  value: "" | "sale" | "rent";
  onChange: (v: "" | "sale" | "rent") => void;
}) {
  const opts: Array<{ key: "" | "sale" | "rent"; label: string; dot?: string }> = [
    { key: "", label: "All" },
    { key: "sale", label: "Sale", dot: "bg-indigo-400" },
    { key: "rent", label: "Rent", dot: "bg-teal-400" },
  ];
  return (
    <div
      className="inline-flex h-9 items-center bg-ink-900/60 border border-white/10 rounded-lg p-0.5 flex-shrink-0"
      role="group"
      aria-label="Filter by deal type"
    >
      {opts.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            aria-pressed={active}
            className={
              "inline-flex items-center gap-1.5 h-full px-2.5 rounded-md text-[12px] font-medium transition-colors " +
              (active
                ? "bg-white/[0.08] text-slate-100"
                : "text-slate-400 hover:text-slate-200")
            }
          >
            {o.dot && (
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  active ? o.dot : "bg-slate-600"
                }`}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Toolbar "filter by assigned broker" chip — thin wrapper around
 *  ``UserSelect`` with the filter-specific empty label. Kept as a
 *  named component so call-sites stay self-documenting. */
function BrokerFilter({
  value,
  onChange,
  teamMembers,
  viewerId,
}: {
  value: string;
  onChange: (v: string) => void;
  teamMembers: TeamMember[];
  viewerId: string | undefined;
}) {
  return (
    <UserSelect
      value={value}
      onChange={onChange}
      users={teamMembers}
      viewerId={viewerId}
      noneLabel="Everyone"
      placeholder="Everyone"
    />
  );
}

function pct(n: number | null): string {
  if (n == null) return "—";
  return `${Math.round(n * 100)}%`;
}

function DealStatsPanel({
  stats,
  expanded,
  onToggle,
  timeRangeLabel,
  viewerId,
  commCcy,
}: {
  stats: DealStats | null;
  expanded: boolean;
  onToggle: () => void;
  timeRangeLabel: string;
  viewerId: string | undefined;
  commCcy: CommissionCcy;
}) {
  const stages = stats?.stages ?? [];
  // Per-stage open snapshot (window-independent). Drives the funnel
  // strip — fed straight from the backend so we don't recompute.
  const openSnapshot = useMemo(() => {
    const map = new Map<DealStage, number>();
    stages.forEach((s) => map.set(s.stage, s.count));
    return map;
  }, [stages]);
  const maxStage = Math.max(1, ...stages.map((s) => s.count));

  return (
    <div className="card border-white/8 bg-ink-900/40 mb-4">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
            Team stats
          </span>
          <span className="text-[11px] text-slate-500">
            · {timeRangeLabel}
          </span>
          {stats && (
            <span className="text-[11px] text-slate-400 tabular-nums">
              <span className="text-emerald-300">{stats.done_count}</span> done
              {" · "}
              <span className="text-rose-300/80">{stats.lost_count}</span> lost
              {" · "}
              <span className="text-slate-300">
                {pct(stats.conversion_rate)}
              </span>{" "}
              close-rate
              {" · "}
              <span className="text-emerald-200">
                <CommissionTotalsView
                  totals={stats.done_value}
                  ccy={commCcy}
                  compact
                />
              </span>{" "}
              earned
            </span>
          )}
        </div>
        <span className="text-slate-500 text-xs">{expanded ? "▾" : "▸"}</span>
      </button>
      {expanded && stats && (
        <div className="px-4 pb-4 border-t border-white/5">
          {/* Headline numbers */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
            <KPI label="Open" value={stats.open_count.toString()} />
            <KPI
              label="Done"
              value={stats.done_count.toString()}
              accent="text-emerald-300"
            />
            <KPI
              label="Lost"
              value={stats.lost_count.toString()}
              accent="text-rose-300/80"
            />
            <KPI label="Close-rate" value={pct(stats.conversion_rate)} />
            <KPI
              label="Avg days to close"
              value={
                stats.avg_days_to_close != null
                  ? `${stats.avg_days_to_close}d`
                  : "—"
              }
            />
          </div>

          {/* Funnel strip — live snapshot, not window-scoped. */}
          <div className="mt-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Funnel snapshot
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {DEAL_STAGES.map((s) => {
                const n = openSnapshot.get(s) ?? 0;
                const w = Math.max(4, (n / maxStage) * 100);
                return (
                  <div key={s} className="flex flex-col">
                    <div className="h-1.5 bg-white/[0.06] rounded-sm overflow-hidden">
                      <div
                        className="h-full bg-accent-500/60"
                        style={{ width: `${w}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-slate-400 tabular-nums mt-1">
                      {STAGE_META[s].label}{" "}
                      <span className="text-slate-200">{n}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sale vs Rent — closed counts honour the time window, open
              is a live snapshot. Always rendered when at least one row
              has any activity so the user can compare types at a glance. */}
          {stats.by_type && stats.by_type.some(
            (r) =>
              r.open_count > 0 ||
              r.done_count > 0 ||
              r.lost_count > 0,
          ) && (
            <div className="mt-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                By type — {timeRangeLabel}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {stats.by_type.map((r) => (
                  <DealTypeCard key={r.deal_type} row={r} commCcy={commCcy} />
                ))}
              </div>
            </div>
          )}

          {/* Per-broker table */}
          {stats.brokers.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                By broker — {timeRangeLabel}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11.5px] text-slate-300 tabular-nums">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                    <tr className="border-b border-white/5">
                      <th className="text-left font-medium py-1.5 pr-2">
                        Broker
                      </th>
                      <th className="text-right font-medium px-2">Open</th>
                      <th className="text-right font-medium px-2">Done</th>
                      <th className="text-right font-medium px-2">Lost</th>
                      <th className="text-right font-medium px-2">Close-rate</th>
                      <th className="text-right font-medium px-2 pl-3">
                        Commission
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.brokers.map((b) => (
                      <tr
                        key={b.user_id}
                        className="border-b border-white/[0.03] last:border-b-0"
                      >
                        <td className="py-1.5 pr-2 text-slate-200">
                          {b.full_name || b.email || "—"}
                          {b.user_id === viewerId && (
                            <span className="ml-1.5 text-[10px] text-slate-500">
                              (you)
                            </span>
                          )}
                          <span className="ml-1.5 text-[10px] text-slate-600">
                            {roleLabel(b.role)}
                          </span>
                        </td>
                        <td className="text-right px-2">{b.open_count}</td>
                        <td className="text-right px-2 text-emerald-300/90">
                          {b.done_count}
                        </td>
                        <td className="text-right px-2 text-rose-300/70">
                          {b.lost_count}
                        </td>
                        <td className="text-right px-2">
                          {pct(b.conversion_rate)}
                        </td>
                        <td className="text-right px-2 pl-3 text-emerald-200/90">
                          <CommissionTotalsView
                            totals={b.done_value}
                            ccy={commCcy}
                            compact
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {expanded && !stats && (
        <div className="px-4 py-3 text-[11px] text-slate-500 border-t border-white/5">
          Loading stats…
        </div>
      )}
    </div>
  );
}

/** One Sale/Rent rollup card in the stats panel. Shows the same KPI
 *  rhythm as the team-wide row (open / done / lost / close-rate / earned)
 *  but scoped to a single deal type. Coloured dot matches the deal-type
 *  chip on the kanban cards so the two read as the same concept. */
function DealTypeCard({
  row,
  commCcy,
}: {
  row: DealTypeStats;
  commCcy: CommissionCcy;
}) {
  const meta = DEAL_TYPE_META[row.deal_type];
  const closed = row.done_count + row.lost_count;
  const closeRate = closed > 0 ? row.done_count / closed : null;
  return (
    <div className={`rounded-md border px-3 py-2.5 ${meta.cls}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          {meta.label}
        </span>
        <span className="text-[10px] text-slate-400">
          · {row.open_count} open
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 tabular-nums">
        <span className="text-[11px]">
          <span className="text-emerald-300">{row.done_count}</span>{" "}
          <span className="text-slate-400">done</span>
        </span>
        <span className="text-[11px]">
          <span className="text-rose-300/80">{row.lost_count}</span>{" "}
          <span className="text-slate-400">lost</span>
        </span>
        <span className="text-[11px]">
          <span className="text-slate-200">{pct(closeRate)}</span>{" "}
          <span className="text-slate-400">close-rate</span>
        </span>
        <span className="text-[11px]">
          <span className="text-emerald-200">
            <CommissionTotalsView totals={row.done_value} ccy={commCcy} compact />
          </span>{" "}
          <span className="text-slate-400">earned</span>
        </span>
      </div>
    </div>
  );
}

function KPI({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={`text-lg font-semibold tabular-nums leading-tight ${accent ?? "text-slate-100"}`}
      >
        {value}
      </div>
    </div>
  );
}

/** Digits only — so "+995 599 12 34 56", "599123456" and "995599123456"
 *  all compare equal. Phones are stored E.164 but brokers type them any
 *  which way, and a literal substring match on the raw string finds
 *  nothing when the two disagree about spaces or the country code. */
function phoneDigits(s: string | null | undefined): string {
  return (s || "").replace(/\D/g, "");
}

/** True if the typed text is meant as a phone number rather than a name. */
function looksLikePhone(q: string): boolean {
  return /^[\d\s+()\-.]+$/.test(q) && phoneDigits(q).length >= 5;
}

interface ClientSelectProps {
  value: string;
  onChange: (id: string) => void;
  /** Preloaded page of clients (first 200). Searched locally for instant
   *  results; the server is queried too so the picker isn't capped at what
   *  happened to be preloaded. */
  clients: Client[];
  /** The deal's current client. Needed as a label fallback: an older deal's
   *  client may not be in the preloaded page, and without this the trigger
   *  would read "— none —" for a deal that plainly has a client. */
  fallback?: DealClientRef | null;
  /** Bubble a newly-created client up so the parent's list carries it too. */
  onClientCreated?: (c: Client) => void;
}

/** Client picker for the deal inspector — type a name OR a phone number.
 *
 *  Replaces a native ``<select>`` over the first 200 clients, which had two
 *  problems: a broker who knows the caller's number had no way to use it,
 *  and any client past the 200th was simply unreachable (including the
 *  already-attached one on an older deal, which silently rendered as
 *  "— none —").
 *
 *  Typing searches locally and on the server (name / phone / email / notes,
 *  the same ``?q=`` the Clients page uses). A number that matches nobody
 *  offers to create the client inline, because that is the whole point of
 *  typing a number you just took down on a call.
 */
function ClientSelect({
  value,
  onChange,
  clients,
  fallback,
  onClientCreated,
}: ClientSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState<Client[]>([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Clients created from inside this picker — kept locally so the trigger
  // can label the pick even if the parent doesn't hold onto them.
  const [created, setCreated] = useState<Client[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pool = useMemo(
    () => [...created, ...clients],
    [created, clients],
  );

  const selected = useMemo(() => {
    if (!value) return null;
    const hit =
      pool.find((c) => c.id === value) ?? remote.find((c) => c.id === value);
    if (hit) return { full_name: hit.full_name, phone: hit.phone };
    if (fallback && fallback.id === value)
      return { full_name: fallback.full_name, phone: fallback.phone };
    return null;
  }, [value, pool, remote, fallback]);

  // Close on outside click / Escape — same behaviour as UserSelect above.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else {
      setQuery("");
      setRemote([]);
      setErr(null);
    }
  }, [open]);

  // Debounced server search. Local filtering covers the preloaded page
  // instantly; this reaches everything past it.
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setRemote([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      api<ClientListResponse>(
        `/api/clients?limit=20&q=${encodeURIComponent(q)}`,
      )
        .then((r) => {
          if (!cancelled) setRemote(r.items);
        })
        .catch(() => {
          if (!cancelled) setRemote([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open]);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qDigits = phoneDigits(query);
    const local = q
      ? pool.filter((c) => {
          if (c.full_name.toLowerCase().includes(q)) return true;
          if (qDigits.length >= 3) {
            const d = phoneDigits(c.phone) + " " + phoneDigits(c.phone_raw);
            if (d.includes(qDigits)) return true;
          }
          return (c.email || "").toLowerCase().includes(q);
        })
      : pool;
    // Local hits first (they're the ones the broker just saw), then any
    // server rows the preloaded page didn't have.
    const seen = new Set<string>();
    const merged: Client[] = [];
    for (const c of [...local, ...remote]) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      merged.push(c);
    }
    return merged.slice(0, 40);
  }, [query, pool, remote]);

  const q = query.trim();
  // Offer inline creation only when nothing already matches exactly —
  // otherwise the broker would be one stray click from a duplicate client.
  const exactHit = options.some(
    (c) =>
      c.full_name.trim().toLowerCase() === q.toLowerCase() ||
      (phoneDigits(q).length >= 5 &&
        (phoneDigits(c.phone) === phoneDigits(q) ||
          phoneDigits(c.phone_raw) === phoneDigits(q))),
  );
  const canCreate = q.length >= 3 && !exactHit && !searching;

  async function createClient() {
    if (!canCreate || creating) return;
    setCreating(true);
    setErr(null);
    try {
      // A typed number becomes both the phone and the placeholder name, so
      // the row is identifiable on the Clients page until someone renames
      // it. Typed text that isn't a number is taken as the name.
      const body = looksLikePhone(q)
        ? { full_name: q, phone: q }
        : { full_name: q };
      const c = await api<Client>("/api/clients", { method: "POST", json: body });
      setCreated((prev) => [c, ...prev]);
      onClientCreated?.(c);
      onChange(c.id);
      setOpen(false);
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "Could not create the client",
      );
    } finally {
      setCreating(false);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={
          inspectorSelectCls +
          " flex items-center gap-2 text-left hover:bg-white/[0.03]"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className="truncate flex-1">
            {selected.full_name}
            {selected.phone && (
              <span className="text-slate-500 tabular-nums">
                {" · "}
                {selected.phone}
              </span>
            )}
          </span>
        ) : (
          <span className="truncate flex-1 text-slate-500">
            — none — <span className="text-slate-600">(type a name or number)</span>
          </span>
        )}
        <svg
          className="w-2.5 h-2.5 text-slate-500 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-40 rounded-lg bg-ink-900 border border-white/10 shadow-card overflow-hidden">
          <div className="p-1.5 border-b border-white/5">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Enter creates when there's nothing to pick — the fast
                // path for "took the number down, move on".
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (options.length === 0 && canCreate) createClient();
                  else if (options.length > 0) {
                    onChange(options[0].id);
                    setOpen(false);
                  }
                }
              }}
              placeholder="Search name or type a phone number…"
              className="w-full bg-ink-850 border border-white/10 rounded-md px-2 py-1.5 text-[12.5px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-accent-500/60"
              autoComplete="off"
            />
          </div>

          {err && (
            <div className="px-2.5 py-2 text-[11.5px] text-rose-300 bg-rose-500/[0.06] border-b border-rose-500/20">
              {err}
            </div>
          )}

          <div className="max-h-[260px] overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className={`w-full px-2.5 py-1.5 text-[12px] text-left hover:bg-white/[0.05] ${
                value === "" ? "text-accent-300" : "text-slate-400"
              }`}
            >
              — none —
            </button>

            {options.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onChange(c.id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-left hover:bg-white/[0.05] ${
                  value === c.id ? "text-accent-300" : "text-slate-300"
                }`}
              >
                <Avatar name={c.full_name} size="xs" />
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{c.full_name}</span>
                  {c.phone && (
                    <span className="block text-[10.5px] text-slate-500 tabular-nums truncate">
                      {c.phone}
                    </span>
                  )}
                </span>
                {value === c.id && (
                  <span className="text-accent-400 text-xs shrink-0">✓</span>
                )}
              </button>
            ))}

            {searching && (
              <div className="px-2.5 py-2 text-[11.5px] text-slate-500">
                Searching…
              </div>
            )}

            {!searching && q.length >= 2 && options.length === 0 && (
              <div className="px-2.5 py-2 text-[11.5px] text-slate-500">
                No client matches “{q}”.
              </div>
            )}

            {canCreate && (
              <button
                type="button"
                onClick={createClient}
                disabled={creating}
                className="w-full mt-1 px-2.5 py-2 text-[12px] text-left border-t border-white/5 text-emerald-300 hover:bg-emerald-500/[0.07] disabled:opacity-50"
              >
                {creating
                  ? "Creating…"
                  : looksLikePhone(q)
                    ? `＋ New client with number ${q}`
                    : `＋ New client “${q}”`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
